import { useCallback, useEffect, useRef } from 'react';

import { contactService } from '@/lib/crm/services';

/** Debounce window before firing the dedup prefetch as the user types the email. */
const EMAIL_DEDUP_PREFETCH_DEBOUNCE_MS = 400;

/**
 * Returns a create-form `onPropertyChange` handler that, on `ahoy_email` changes,
 * debounces a call to `contactService.prefetchExistingByEmail`. This warms the
 * dedup cache while the user is still filling the form so `contactService.create`
 * can skip its pre-submit existence lookup — moving that round-trip off the submit
 * critical path (FE4).
 *
 * Non-email fields are ignored, nothing fires until `orgId` is known, rapid edits
 * collapse into a single trailing prefetch, and a pending timer is cancelled on
 * unmount. Fire-and-forget: `prefetchExistingByEmail` swallows its own errors.
 */
export function useContactEmailDedupPrefetch(
  orgId: number | undefined
): (propertyTypeName: string, value: unknown) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  return useCallback(
    (propertyTypeName: string, value: unknown) => {
      if (propertyTypeName !== 'ahoy_email' || orgId === undefined) {
        return;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        void contactService.prefetchExistingByEmail(orgId, value);
      }, EMAIL_DEDUP_PREFETCH_DEBOUNCE_MS);
    },
    [orgId]
  );
}
